/**
 * T318 — /expenses/export (US7 MVP).
 */
import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import { Button, EmptyState, GlassPanel, Input, LoadingSpinner, PageHeader, Select, StatusBadge } from '../components/ui';
import {
  useExportJobs,
  useCreateExportJob,
  downloadExportUrl,
  type ExportJobDto,
} from '../services/api/exports';
import { useToastHelpers } from '../components/ui/Toast';
import { formatDateBerlin, formatDateTimeBerlin } from '../lib/format-date';

export const Route = createFileRoute('/expenses_/export')({
  component: ExportPage,
});

function ExportPage() {
  return (
    <ProtectedRoute>
      <AppShell>
        <ExportInner />
      </AppShell>
    </ProtectedRoute>
  );
}

function ExportInner() {
  const { data, isLoading } = useExportJobs();
  const createJob = useCreateExportJob();
  const toast = useToastHelpers();

  const [format, setFormat] = useState<'csv' | 'datev_extf_v700' | 'zip_bundle'>(
    'datev_extf_v700'
  );
  const [dateBasis, setDateBasis] = useState<'invoiceDate' | 'paymentDate'>('invoiceDate');
  const [periodFrom, setPeriodFrom] = useState('2026-01-01');
  const [periodTo, setPeriodTo] = useState('2026-03-31');

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      const job = await createJob.mutateAsync({
        format,
        dateBasis,
        periodFrom,
        periodTo,
        includePdfs: format === 'zip_bundle',
      });
      toast.success(`Export erstellt — ${job.expenseCount} Belege`);
    } catch (err) {
      toast.error(`Export fehlgeschlagen: ${(err as Error).message}`);
    }
  }

  return (
    <div className="space-y-8">
      <PageHeader
        title="Export"
        subtitle="CSV · DATEV-EXTF-V700 · ZIP-Bundle mit PDFs — Eingangs-Belege (Ausgaben)"
        actions={
          <Link to="/expenses">
            <Button variant="ghost">Zur Liste</Button>
          </Link>
        }
      />

      {/* A10: Querverweis auf die zweite Export-Fläche */}
      <p className="text-sm text-white/40 -mt-4">
        Ausgangsrechnungen, Kunden und Angebote exportierst du unter{' '}
        <Link to="/export" className="text-white/70 underline hover:text-white">
          Einstellungen → Datenexport
        </Link>
        .
      </p>

      {/* 006b P9: gleiche Control-Primitives wie /export (ui/Select) +
          GlassPanel statt naked box (Kanon §6/§9) */}
      <GlassPanel>
        <form onSubmit={onSubmit} className="space-y-4">
          <p className="text-sm text-white/60">Der Zeitraum folgt der gewählten Datumsbasis. Bei Zahlungsdatum werden Belege ohne Zahlungstag nicht zugeordnet. Nur eingereichte/geprüfte Belege werden exportiert.</p>
          <Select label="Datumsbasis" value={dateBasis} onChange={(e) => setDateBasis(e.target.value as typeof dateBasis)} hint="Zahlungsdatum steht für den internen CSV-Abgleich zur Verfügung. DATEV und ZIP verwenden Rechnungsdatum." options={[{ value: 'invoiceDate', label: 'Rechnungsdatum' }, { value: 'paymentDate', label: 'Zahlungsdatum', disabled: format !== 'csv' }]} />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Select
              label="Format"
              value={format}
              onChange={(e) => {
                const nextFormat = e.target.value as typeof format;
                setFormat(nextFormat);
                if (nextFormat !== 'csv') setDateBasis('invoiceDate');
              }}
              options={[
                { value: 'csv', label: 'CSV (Excel/Tabellenkalkulation)' },
                { value: 'datev_extf_v700', label: 'DATEV-EXTF-V700 (für Steuerberater)' },
                { value: 'zip_bundle', label: 'ZIP-Bundle (CSV + DATEV + PDFs)' },
              ]}
            />
            <Input
              label="Von"
              type="date"
              value={periodFrom}
              onChange={(e) => setPeriodFrom(e.target.value)}
            />
            <Input
              label="Bis"
              type="date"
              value={periodTo}
              onChange={(e) => setPeriodTo(e.target.value)}
            />
          </div>
          <Button type="submit" disabled={createJob.isPending}>
            {createJob.isPending ? 'Erstelle…' : 'Export erstellen'}
          </Button>
        </form>
      </GlassPanel>

      <div>
        <h2 className="text-lg font-medium text-white mb-3">Bisherige Exports</h2>
        {/* 006b P4/P8: Liste in Kanon-Surface + shared EmptyState */}
        <GlassPanel variant="subtle" padding="sm" className="overflow-hidden">
          {isLoading && <LoadingSpinner />}
          {data?.items.length === 0 && (
            <EmptyState
              title="Keine Exports bisher"
              description="Erstelle oben deinen ersten Export."
            />
          )}
          <div className="divide-y divide-white/10">
            {data?.items
              .slice()
              .reverse()
              .map((j) => (
                <ExportRow key={j.id} job={j} />
              ))}
          </div>
        </GlassPanel>
      </div>
    </div>
  );
}

const FORMAT_LABELS: Record<string, string> = {
  csv: 'CSV',
  datev_extf_v700: 'DATEV-EXTF-V700',
  zip_bundle: 'ZIP-Bundle',
};

function ExportRow({ job }: { job: ExportJobDto }) {
  const expired =
    job.expiresAt != null && new Date(job.expiresAt).getTime() < Date.now();
  return (
    // 006b P4: Zeile INNERHALB der Surface — rahmenlos, divide-y macht die Trennung
    <div className="flex justify-between items-center gap-3 p-3">
      <div>
        <div className="text-white flex items-center gap-2 flex-wrap">
          <span>
            {FORMAT_LABELS[job.format]} · {job.filtersJson?.dateBasis === 'paymentDate' ? 'Zahlungsdatum' : 'Rechnungsdatum'} · {formatDateBerlin(job.periodFrom)} →{' '}
            {formatDateBerlin(job.periodTo)}
          </span>
          <StatusBadge
            tone={job.status === 'ready' ? 'success' : job.status === 'failed' ? 'danger' : 'info'}
          >
            {job.status}
          </StatusBadge>
        </div>
        <div className="text-white/50 text-sm">
          {job.expenseCount} Belege · {formatDateTimeBerlin(job.createdAt)}
          {job.outputByteSize != null && ` · ${Math.round(job.outputByteSize / 1024)} KB`}
        </div>
      </div>
      {job.status === 'ready' && !expired && (
        <a
          href={downloadExportUrl(job.id)}
          download
          className="px-3 py-1.5 text-sm rounded-lg bg-blue-500/20 border border-blue-500/30 text-blue-100 hover:bg-blue-500/30 shrink-0"
        >
          Herunterladen
        </a>
      )}
      {expired && <span className="text-xs text-white/40 shrink-0">Abgelaufen</span>}
    </div>
  );
}
